<template>
  <div
    class="admin-search"
    style="margin-top: 20px"
    :class="{ 'has-results': totalResultsCount > 0 }"
  >
    <div class="row">
      <div class="col-sm-8">
        <h3 v-if="totalResultsCount > 0">Search results:</h3>
        <h4 v-if="model && totalResultsCount == 0">No results for '{{ query }}'.</h4>
      </div>
      <form class="col-sm-4" v-on:submit.prevent="performSearch" autocomplete="off">
        <div
          class="input-wrapper form-control form-control-lg search-input-wrapper"
          :class="{ loading: !!serverCallTimeout || isLoading }"
        >
          <input class="" type="text" v-model="query" placeholder="Search..." />
        </div>
      </form>
    </div>
    <div class="row"></div>
    <div class="search-results-wrapper" v-if="model">
      <div class="row" v-if="model.Organisations.length > 0">
        <div class="col-sm-12">
          <h4>Organisations</h4>
          <div
            class="search-result group-search-result"
            v-for="organisation in model.Organisations"
            v-bind:key="organisation.id"
          >
            <a :href="'/organisation/details/' + organisation.id">{{ organisation.name }}</a>
          </div>
        </div>
      </div>
      <div class="row" v-if="model.Teams.length > 0">
        <div class="col-sm-12">
          <h4>Teams</h4>
          <div
            class="search-result group-search-result"
            v-for="group in model.Teams"
            v-bind:key="group.id"
          >
            <a :href="'/admin/group/details/' + group.id">{{ group.name }}</a>
            <span v-if="group.organisation" class="label label-default">{{
              group.organisation.name
            }}</span>
          </div>
        </div>
      </div>
      <div class="row" v-if="model.OtherGroups.length > 0">
        <div class="col-sm-12">
          <h4>Other groups</h4>
          <div
            class="search-result group-search-result"
            v-for="group in model.OtherGroups"
            v-bind:key="group.id"
          >
            <a :href="'/admin/group/details/' + group.id">{{ group.name }}</a>
            <span v-if="group.organisation" class="label label-default">{{
              group.organisation.name
            }}</span>
          </div>
        </div>
      </div>
      <div class="row" v-if="model.Users.length > 0">
        <div class="col-sm-12">
          <h4>Users</h4>
          <div
            class="search-result user-search-result"
            v-for="user in model.Users"
            v-bind:key="user.id"
          >
            <a :href="'/admin/user/details/' + user.id"
              >{{ user.firstName }} {{ user.lastName }} &lt;{{ user.email }}&gt;</a
            >
            <span v-if="user.organisation" class="label label-default">{{
              user.organisation.name
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { debug as d } from "debug";
import { computed, onMounted, ref, watch } from "vue";
import {
  GroupDescriptionApiModel,
  OrganisationDescriptionApiModel,
  ShortUserDetailsApiModel,
} from "../../api-models";

const debug = d("cs.AdminSearchPage");

export interface AdminSearchResults {
  Users: ShortUserDetailsApiModel[];
  Teams: GroupDescriptionApiModel[];
  OtherGroups: GroupDescriptionApiModel[];
  Organisations: OrganisationDescriptionApiModel[];
}

const query = ref<string>("");
const model = ref<AdminSearchResults | null>(null);
const serverCallTimeout = ref<any>(null);
const isLoading = ref<boolean>(false);

const totalResultsCount = computed(() => {
  if (!model.value) {
    return 0;
  }

  const orgCount = model.value.Organisations.length;
  const userCount = model.value.Users.length;
  const teamsCount = model.value.Teams.length;
  const otherGroupsCount = model.value.OtherGroups.length;
  return orgCount + userCount + teamsCount + otherGroupsCount;
});

function performSearch(): void {
  if (!query.value || query.value.trim().length < 3) {
    model.value = null;
    return;
  }

  // Simple debounce to avoid hammering the server
  if (serverCallTimeout.value) {
    clearTimeout(serverCallTimeout.value);
  }
  serverCallTimeout.value = setTimeout(() => {
    const queryValue = query.value.trim();
    debug("Searching", queryValue);
    isLoading.value = true;
    serverCallTimeout.value = null;
    axios.get(`/admin/search/query?q=${queryValue}`).then((resp) => {
      const modelValue = resp.data as AdminSearchResults;
      model.value = modelValue;
      debug(`Got results for: ${queryValue}`);
      isLoading.value = false;
    });
  }, 500);
}

watch(query, () => {
  performSearch();
});

onMounted(() => {
  debug("Mounted");
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
.input-wrapper {
  height: 48px;
  line-height: 32px;
  width: 100% !important;
}

.input-wrapper input {
  border-width: 0 !important;
  font-size: 18px;
  outline: none !important;
  width: 100%;
  min-width: 100%;
}

.search-input-wrapper.loading::before {
  content: "...";
  position: absolute;
  right: 100%;
  top: 0;
  line-height: 42px;
}

.admin-search {
  padding-left: 40px;
  margin-bottom: 50px;
}

.admin-search.has-results {
  border-left: 1px solid lightgray;
}

.search-result {
  margin-bottom: 5px;

  .label {
    margin-left: 5px;
  }
}
</style>
